import { Button, Checkbox, Group, Input, ScrollArea, Skeleton, Text, Tooltip } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { closeAllModals } from '@mantine/modals';
import cx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { bindBotDatasets, getDatasetsList, useBotStore, DatasetsInfo } from '@mygpt/app';
import { SvgSearch } from '@/components/Common/icon';
import { DEFAULT_MODEL_NAME } from '@/components/Create_V2/ModelSelect';
import { SvgDataset } from '@/components/Embeddings/icon';

import classes from './index.module.css';

export const BindContent = ({ id, initList, onBind }: { id: string; initList: boolean; onBind: () => void }) => {
  const { t } = useTranslation();

  const isLocalMode = import.meta.env.VITE_MODE === 'local';

  const [loading, setLoading] = useState(false);
  const [datasets, setDatasets] = useState<DatasetsInfo[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bindLoading, setBindLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const { linkedDataset } = useBotStore();

  const isEmptyList = useMemo(() => {
    return !linkedDataset.length && !initList;
  }, [linkedDataset, initList]);

  const modelName = useMemo(() => {
    if (linkedDataset.length) {
      return linkedDataset[0].metadata.embedding_model_name
        ? linkedDataset[0].metadata.embedding_model_name
        : DEFAULT_MODEL_NAME;
    }
    return '';
  }, [linkedDataset]);

  useEffect(() => {
    setLoading(true);
    getDatasetsList({
      size: 1000,
    })
      .then((res) => {
        let datasetList = [];
        //如果关联的知识库为空且非本地部署时，展示全部，否则过滤掉不同模型的知识库
        if (!isEmptyList && !isLocalMode) {
          datasetList = res.data.items.filter((item: DatasetsInfo) => checkModel(item, 'list'));
        } else {
          datasetList = res.data.items;
        }

        setDatasets(datasetList);
      })
      .finally(() => {
        setLoading(false);
      })
      .catch((err) => {});
  }, []);

  const handleBind = ({ type }: { type: 'bind' | 'unbind' }) => {
    setBindLoading(true);
    bindBotDatasets({
      aiId: id || '',
      datasetIds: selectedIds,
      type: 'bind',
    })
      .then((res) => {
        closeAllModals();
        onBind();
      })
      .finally(() => {
        setBindLoading(false);
      })
      .catch((err) => {});
  };

  const checkExist = (item: DatasetsInfo) => {
    return item.robots.some((robot) => robot.id === id);
  };

  const checkModel = (item: DatasetsInfo, type: 'list' | 'select') => {
    const model = item.metadata.embedding_model_name || DEFAULT_MODEL_NAME;
    return type === 'list' ? model === modelName : model === selectedModel;
  };

  // 根据搜索关键词过滤知识库列表
  const filteredDatasets = useMemo(() => {
    if (!debouncedSearch) return datasets;
    const searchLower = debouncedSearch.toLowerCase();
    return datasets.filter((item) => item.name.toLowerCase().includes(searchLower));
  }, [datasets, debouncedSearch]);

  return (
    <div>
      {/* 搜索框 - 放在 scrollContainer 外部，避免 CSS 干扰 */}
      <Input
        size="sm"
        className="w-full mb-4"
        leftSection={<SvgSearch />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        placeholder={t('embeddings.dataset.add_modal.search_placeholder')}
        rightSectionPointerEvents="auto"
        rightSection={
          search ? (
            <button onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
              ×
            </button>
          ) : null
        }
      />
      <div className={cx('py-2 mb-4', classes.scrollContainer)}>
        {!isLocalMode && (
          <Group className="mb-4" gap={0}>
            {!isEmptyList && (
              <Text size="sm">
                <Trans i18nKey="embeddings.dataset.add_modal.model" values={{ name: modelName }}>
                  <span className="text-violet-600"></span>
                </Trans>
              </Text>
            )}
            <Text size="sm">{t('embeddings.dataset.add_modal.same_model_tip')}</Text>
          </Group>
        )}

        {loading ? (
          <div>
            <Skeleton height={8} width="70%" />
            <Skeleton height={8} mt={6} />
            <Skeleton height={8} width="50%" mt={6} />
          </div>
        ) : (
          <ScrollArea.Autosize
            mah={500}
            styles={(theme) => {
              return {
                viewport: {
                  '& > div': {
                    display: 'block !important',
                  },
                },
              };
            }}
          >
            <ul className="flex flex-wrap gap-3">
              {filteredDatasets.length > 0 &&
                filteredDatasets.map((item) => {
                  return (
                    <li
                      className="border border-gray-200 rounded-md p-3 w-full sm:w-[48%] cursor-pointer hover:bg-violet-50"
                      key={item.id}
                      onClick={() => {
                        if (checkExist(item)) return;
                        const newIds = selectedIds.includes(item.id)
                          ? selectedIds.filter((id) => id !== item.id)
                          : [...selectedIds, item.id];

                        setSelectedIds(newIds);

                        if (newIds.length > 0) {
                          setSelectedModel(item.metadata.embedding_model_name || DEFAULT_MODEL_NAME);
                        } else {
                          setSelectedModel('');
                        }
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap" className="h-full">
                        <Group wrap="nowrap" className="grow overflow-hidden">
                          <div className=" flex-shrink-0">
                            <SvgDataset />
                          </div>
                          <div className="grow overflow-hidden max-w-[240px]">
                            <Tooltip label={item.name} withArrow openDelay={300} transitionProps={{ duration: 200 }}>
                              <p className="truncate">{item.name}</p>
                            </Tooltip>
                            <Tooltip
                              label={item.description}
                              withArrow
                              openDelay={300}
                              transitionProps={{ duration: 200 }}
                            >
                              <p className="text-gray-400 text-sm truncate">{item.description}</p>
                            </Tooltip>
                          </div>
                        </Group>
                        <Checkbox
                          checked={selectedIds.includes(item.id)}
                          onChange={() => {}}
                          disabled={
                            // 本地部署时只检查知识库是否已关联，不过滤相同模型
                            checkExist(item) || (!isLocalMode && !!(selectedModel && !checkModel(item, 'select')))
                          }
                        />
                      </Group>
                    </li>
                  );
                })}
            </ul>
          </ScrollArea.Autosize>
        )}
      </div>
      <Group justify="space-between">
        <span>
          <Trans i18nKey={'embeddings.dataset.add_modal.selected'} values={{ num: selectedIds.length }}>
            <span className="text-violet-500" />
          </Trans>
        </span>
        <div>
          <Button size="sm" variant="outline" color="red" onClick={() => closeAllModals()}>
            {t('alert_cancel')}
          </Button>
          <Button
            size="sm"
            className="ml-2"
            loading={bindLoading}
            onClick={() => {
              handleBind({ type: 'bind' });
            }}
            disabled={selectedIds.length === 0}
          >
            {t('embeddings.dataset.add_modal.add_btn')}
          </Button>
        </div>
      </Group>
    </div>
  );
};
